import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Link, useLocation, useMatch, useSearchParams } from 'react-router-dom'

import { useBrand } from '../../contexts/BrandContext'
import { useHeaderVisibility } from '../../contexts/HeaderVisibilityContext'
import { useMediaQueryContext } from '../../contexts/MediaQueryContext'
import LoginButton from '../Auth/LoginButton'
import { SideNav } from './SideNav'

const routesWithNav = [
  '/',
  '/featured',
  '/favorites',
  '/recent',
  '/search',
  '/search',
  '/mentors',
  '/pricing',
  '/account',
  '/account/billing',
]

export default function Header() {
  const location = useLocation()

  const brand = useBrand()

  const { isAuthenticated } = useAuth()
  const matchSession = useMatch('/:sessionId')
  const [searchParams] = useSearchParams()
  const startParam = searchParams.get('start')

  const { is2lg: isMobile } = useMediaQueryContext()

  const [isVisible, setIsVisible] = useState(true)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { setIsHeaderVisible } = useHeaderVisibility()

  useEffect(() => {
    if (!isAuthenticated) {
      document.body.classList.add('tc-mv-main-nav-hidden')
      return
    }
    document.body.classList.remove('tc-mv-main-nav-hidden')
  }, [isAuthenticated])

  const showNav = useMemo(
    () =>
      location.pathname === '/' || routesWithNav.includes(location.pathname),
    [location.pathname],
  )

  const handleInteraction = () => {
    setIsVisible(true)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, 5000)
  }

  useEffect(() => {
    if (matchSession && startParam) {
      handleInteraction()
      document.addEventListener('mousemove', handleInteraction)
      document.addEventListener('touchstart', handleInteraction)
    } else {
      setIsVisible(true)
      document.removeEventListener('mousemove', handleInteraction)
      document.removeEventListener('touchstart', handleInteraction)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }

    return () => {
      document.removeEventListener('mousemove', handleInteraction)
      document.removeEventListener('touchstart', handleInteraction)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [matchSession, startParam])

  useEffect(() => {
    setIsHeaderVisible(isVisible)
  }, [isVisible, setIsHeaderVisible])

  return (
    <div
      data-testid="user-menu-popup"
      className={`tc-mv-header ${
        !showNav || !isAuthenticated ? 'without-nav' : ''
      }`}
    >
      <div className="flex relative tc-mv-header-container">
        {/* bg should be defferend div, othervice it broke bg filters for dropdowns */}
        <div
          className={`tc-mv-blur absolute top-0 left-0 right-0 bottom-0 transition-opacity duration-300 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div className="flex-grow-0 flex-shrink-0 align-start basis-auto items-center relative tc-mv-header-left">
          {isAuthenticated && showNav && <SideNav isMobile={isMobile} />}

          <Link to="/" className="tc-mv-header-logo">
            <div className="flex align-middle items-center">
              <img
                src={brand.logo}
                alt="logo"
                className="!h-[22px] md:!h-[30px]"
              />
            </div>
          </Link>
        </div>
        <div
          className={`tc-mv-header-right align-end relative transition-opacity duration-300 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <LoginButton />
        </div>
      </div>
    </div>
  )
}
